import {singleton} from "tsyringe";
import {
    distinctUntilChanged,
    filter,
    fromEvent,
    map,
    merge,
    Observable,
    share,
    Subject,
    takeUntil,
    throttleTime
} from "rxjs";

type ElementTouchMetaData = { threshold: number, coordinates: Coordinates, remainder: { x: number, y: number } };
type Coordinates = { x: number, y: number };
export type DraggingCoordinates = { x: number, y: number, remainder: { x: number, y: number } };

@singleton()
export class TouchService {

    private _channel = new Subject<DraggingCoordinates>();
    private _elementMetaData = new Map<Node, ElementTouchMetaData>();
    private _stopListen$ = new Subject<void>();

    private _channel$ = this._channel.pipe(
        share()
    );

    public get channel$(): Observable<DraggingCoordinates> {
        return this._channel$;
    }

    public register(element: HTMLElement, threshold: number = 35): void {
        fromEvent<TouchEvent, Coordinates>(
            element,
            `touchstart`,
            {passive: true},
            evt => (<Coordinates>{x: evt.touches[0].clientX, y: evt.touches[0].clientY})
        ).pipe(
            distinctUntilChanged((previous, current) => {
                return previous.x === current.x && previous.y === current.y
            }),
            // only start if in a cell
            filter(({x, y}) => {
                return document
                    .elementsFromPoint(x, y)
                    .some((element) => element.tagName === `CELL`)
            })
        ).subscribe((coordinates: Coordinates): void => {
            const elementMetaData = {
                threshold,
                coordinates,
                remainder: {
                    x: 0,
                    y: 0
                }
            };

            if (this._elementMetaData.has(element)) {
                elementMetaData.remainder = this._elementMetaData.get(element)!.remainder;
            }
            this._elementMetaData.set(element, elementMetaData);

            this._startListenTouchMove(element);
        });
    }

    private _startListenTouchMove(element: Node): void {
        merge(
            fromEvent(element, `touchcancel`, {passive: true}, (evt: TouchEvent) => (<Coordinates>{
                x: evt.changedTouches[0].clientX,
                y: evt.changedTouches[0].clientY
            })),
            fromEvent(element, `touchend`, {passive: true}, (evt: TouchEvent) => (<Coordinates>{
                x: evt.changedTouches[0].clientX,
                y: evt.changedTouches[0].clientY
            }))
        ).pipe(
            takeUntil(this._stopListen$),
        ).subscribe((coordinates: Coordinates) => {
            if (this._elementMetaData.has(element)) {
                const elementMetaData = this._elementMetaData.get(element)!;
                elementMetaData.remainder.x = Math.abs(elementMetaData.remainder.x + coordinates.x - elementMetaData.coordinates.x);
                elementMetaData.remainder.y = coordinates.y - elementMetaData.coordinates.y;
            }
            this._stopListen$.next();
        });

        fromEvent<TouchEvent, Coordinates>(
            element,
            `touchmove`,
            {passive: true},
            evt => (<Coordinates>{
                x: evt.touches[0].clientX,
                y: evt.touches[0].clientY
            })
        )
            .pipe(
                distinctUntilChanged((previous, current) => {
                    return previous.x === current.x && previous.y === current.y
                }),
                throttleTime(50),
                takeUntil(this._stopListen$),
                map<Coordinates, DraggingCoordinates>(coordinates => {
                    const elementMetaData = this._elementMetaData.get(element)!;
                    // delta von Startposition von x und aktueller Position von x
                    const deltaX = coordinates.x - elementMetaData.coordinates.x;
                    const firstGridColumnWidth = elementMetaData.remainder.x + deltaX


                    let stepX = 0;
                    if (firstGridColumnWidth >= elementMetaData.threshold) {
                        // es wurde Pointer nach rechts geschoben -> somit stepX muss größer 0 sein
                        stepX = Math.floor(firstGridColumnWidth / elementMetaData.threshold);
                        elementMetaData.coordinates.x = coordinates.x;
                        elementMetaData.remainder.x = Math.abs(firstGridColumnWidth % elementMetaData.threshold);
                    }

                    if (firstGridColumnWidth < 0) {
                        const _firstGridColumnWidth = elementMetaData.threshold + firstGridColumnWidth;
                        // es wurde der Pointer nach links geschoben -> somit muss stepX kleiner 0 sein
                        stepX = -1 * Math.ceil(_firstGridColumnWidth / elementMetaData.threshold);
                        elementMetaData.coordinates.x = coordinates.x;
                        elementMetaData.remainder.x = Math.abs(_firstGridColumnWidth % elementMetaData.threshold);
                    }

                    // -- hoch/runter
                    const deltaY = coordinates.y - elementMetaData.coordinates.y;
                    const relativeDeltaY = deltaY / elementMetaData.threshold;
                    const stepY = Math[relativeDeltaY < 0 ? `ceil` : `floor`](relativeDeltaY);
                    if (stepY != 0) {
                        elementMetaData.coordinates.y = coordinates.y;
                    }

                    return <DraggingCoordinates>{
                        x: stepX,
                        y: -1 * stepY,
                        remainder: {
                            x: stepX > 0 ? Math.abs(firstGridColumnWidth % elementMetaData.threshold) : Math.abs((elementMetaData.threshold + firstGridColumnWidth) % elementMetaData.threshold),
                            y: deltaY - elementMetaData.threshold * stepY,
                        }
                    };
                })
            )
            .subscribe(value => {
                this._channel.next(value);
            });
    }
}